/**
 * 三欄左側的「清單上方那一疊」：建帳號、建帳號錯誤、失效身分提示、關鍵字提醒。
 *
 * 這是**內容層**，不是外殼：沿用 console-ui 既有的 `.cui-*` class（顏色已全部吃 `--ak-*`），
 * 不動任何行為與 `data-testid`，只把它從 App.tsx 搬出來。
 */
import type { ConsoleActions } from '../../hooks/useConsoleActions';

export interface LeftRailProps {
  console: ConsoleActions;
}

export function LeftRail({ console: c }: LeftRailProps) {
  return (
    <div className="flex flex-none flex-col gap-1">
      <button
        type="button"
        className="cui-btn cui-btn--create"
        onClick={c.createAccount}
        disabled={c.busy}
        data-testid="create-account"
      >
        ＋ 建帳號
      </button>

      {/*
        失敗原因由內容區最上面的「開通」區塊統一顯示（同一件事不要講兩次，而且那裡會翻成人話）。
        這裡只在沒有那個區塊時才留舊的紅字，避免原始錯誤字串（端點、狀態碼）直接出現在左欄。
      */}
      {c.createError && !c.opening ? (
        <p className="cui-create-error" role="status" data-testid="create-error">
          {c.createError}
        </p>
      ) : null}

      {c.dropped.length > 0 ? (
        <div className="cui-dropped" data-testid="dropped-sessions" role="status">
          {c.dropped.map((session) => (
            <div key={session.id} className="cui-dropped-row" data-testid="dropped-row">
              <span className="cui-dropped-text">
                {session.nickname} 已失效，已從清單移除
                <span className="cui-mono cui-muted">（{session.reason}）</span>
              </span>
              <button
                type="button"
                className="cui-btn cui-btn--sm"
                onClick={() => c.rebuildDropped(session)}
                disabled={c.busy}
                data-testid="dropped-rebuild"
              >
                重建
              </button>
              <button
                type="button"
                className="cui-icon-btn"
                onClick={() => c.dismissGone(session.id)}
                aria-label="知道了"
                data-testid="dropped-dismiss"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <details className="cui-kw" data-testid="highlight-settings">
        <summary className="cui-kw-summary">
          關鍵字提醒
          <span className="cui-kw-count" data-testid="highlight-count">
            {c.highlightKeywords.length}
          </span>
        </summary>
        <p className="cui-kw-hint">
          訊息含這些字就標色；命中的未讀對話會排到最上面。暱稱自動包含。
        </p>
        <div className="cui-kw-chips">
          {c.highlights.nicknames.map((nickname) => (
            <span key={`n:${nickname}`} className="cui-kw-chip is-auto" title="自動：自己的暱稱">
              {nickname}
            </span>
          ))}
          {c.highlights.custom.map((keyword) => (
            <span key={`c:${keyword}`} className="cui-kw-chip">
              {keyword}
              <button
                type="button"
                className="cui-kw-remove"
                onClick={() => c.highlights.remove(keyword)}
                aria-label={`移除關鍵字 ${keyword}`}
                data-testid="highlight-remove"
              >
                ×
              </button>
            </span>
          ))}
          {c.highlightKeywords.length === 0 ? (
            <span className="cui-kw-hint">還沒有關鍵字</span>
          ) : null}
        </div>
        <div className="cui-kw-form">
          <input
            className="cui-input cui-kw-input"
            value={c.keywordDraft}
            onChange={(event) => c.setKeywordDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              c.submitKeyword();
            }}
            placeholder="加關鍵字後按 Enter"
            aria-label="新增關鍵字"
            data-testid="highlight-input"
          />
          <button
            type="button"
            className="cui-btn cui-btn--sm"
            onClick={c.submitKeyword}
            disabled={c.keywordDraft.trim() === ''}
            data-testid="highlight-add"
          >
            加入
          </button>
        </div>
      </details>
    </div>
  );
}
